import { faqItems } from "@/pages/help/faqData";
import { getRouteMeta, siteName, siteUrl, type RouteMeta } from "@/router/routeMeta";

type SchemaObject = Record<string, unknown>;

function toAbsoluteUrl(path: string) {
  return `${siteUrl}${path === "/" ? "" : path}`;
}

function buildBreadcrumbItems(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: siteName,
      item: siteUrl,
    },
  ];

  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: getRouteMeta(currentPath).title,
      item: toAbsoluteUrl(currentPath),
    });
  });

  return items;
}

export function buildOrganizationSchema(): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: siteUrl,
    description: "面向高考考生和家长的志愿填报辅助平台，提供 AI 志愿咨询、院校与专业数据、分数线参考、资讯和入学前指南。",
  };
}

export function buildWebsiteSchema(): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: siteUrl,
    description: "帮助高考考生和家长查询院校、专业、分数线与政策，并通过 AI 问答辅助判断志愿方向。",
  };
}

export function buildFaqSchema(): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export function buildCollectionSchema(meta: RouteMeta, pathname: string): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: meta.title,
    description: meta.description,
    url: toAbsoluteUrl(meta.canonicalPath ?? pathname),
  };
}

export function buildWebPageSchema(meta: RouteMeta, pathname: string): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: meta.title,
    description: meta.description,
    url: toAbsoluteUrl(meta.canonicalPath ?? pathname),
  };
}

export function buildBreadcrumbSchema(pathname: string): SchemaObject {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: buildBreadcrumbItems(pathname),
  };
}

export function getSchemasForRoute(pathname: string) {
  const meta = getRouteMeta(pathname);
  const schemas: SchemaObject[] = [];
  const kinds = meta.schema?.map((item) => item.kind) ?? [];

  kinds.forEach((kind) => {
    switch (kind) {
      case "organization":
        schemas.push(buildOrganizationSchema());
        break;
      case "website":
        schemas.push(buildWebsiteSchema());
        break;
      case "faq":
        schemas.push(buildFaqSchema());
        break;
      case "collection":
        schemas.push(buildCollectionSchema(meta, pathname));
        break;
      case "webpage":
        schemas.push(buildWebPageSchema(meta, pathname));
        break;
      case "breadcrumbs":
        schemas.push(buildBreadcrumbSchema(pathname));
        break;
      default:
        break;
    }
  });

  return schemas;
}
